import type {
  BoardPayloadAssignee,
  BoardPayloadIssueItem,
  BoardPayloadIssueType,
  BoardPayloadItem,
  BoardPayloadLabel,
  BoardPayloadMilestone,
  BoardPayloadRepository,
  BoardPayloadSelectOption,
  CustomFieldIdentifier,
} from './board-payload'
import {isBoardPayloadSelectOption} from './board-payload'
import {FieldDataType, type FieldDefinition, FieldIdentifier} from '../../saved-view-options/fields'

/** One visible single-select field with an option chosen on this item. */
export interface CardSingleSelect {
  identifier: string
  option: BoardPayloadSelectOption
}

/**
 * One visible multi-select field with at least one option chosen on this item. Its options keep
 * the order the server sends, which is the field editor's order.
 */
export interface CardMultiSelect {
  identifier: string
  options: readonly BoardPayloadSelectOption[]
}

/** The item's visible custom issue fields that have a value, by type, each in `fieldDefinitions` order. */
export interface CardIssueFields {
  singleSelect: readonly CardSingleSelect[]
  multiSelect: readonly CardMultiSelect[]
}

/**
 * The attributable field values a card should render, already gated by which fields are
 * visible. A field that is hidden resolves to its empty value (`[]` or `null`) so the card
 * can render without re-checking visibility.
 */
export interface VisibleCardFields {
  labels: readonly BoardPayloadLabel[]
  assignees: readonly BoardPayloadAssignee[]
  milestone: BoardPayloadMilestone | null
  repository: BoardPayloadRepository | null
  /** Always `null` on a pull request, which has no issue type. */
  issueType: BoardPayloadIssueType | null
  /** Always empty on a pull request, which has no custom fields. */
  issueFields: CardIssueFields
  /** ISO 8601 timestamps. `closed` is `null` until the item is closed or merged. */
  created: string | null
  updated: string | null
  closed: string | null
}

/**
 * Resolves the attributable field values a card should render from a payload item, keeping
 * only the fields named in `visibleFieldIdentifiers`. Custom fields are found through
 * `fieldDefinitions`, since only the definition says what type of value a `field.<id>` holds.
 */
export function resolveVisibleCardFields(
  item: BoardPayloadItem,
  visibleFieldIdentifiers: ReadonlySet<string>,
  fieldDefinitions: readonly FieldDefinition[] = [],
): VisibleCardFields {
  const isVisible = (identifier: string) => visibleFieldIdentifiers.has(identifier)

  return {
    labels: isVisible(FieldIdentifier.LABEL) ? (item.fields.label?.value ?? []) : [],
    assignees: isVisible(FieldIdentifier.ASSIGNEE) ? (item.fields.assignee?.value ?? []) : [],
    milestone: isVisible(FieldIdentifier.MILESTONE) ? (item.fields.milestone?.value ?? null) : null,
    repository: isVisible(FieldIdentifier.REPOSITORY) ? (item.fields.repository?.value ?? null) : null,
    issueType:
      item.type === 'issue' && isVisible(FieldIdentifier.ISSUE_TYPE) ? (item.fields.issue_type?.value ?? null) : null,
    issueFields:
      item.type === 'issue'
        ? resolveIssueFields(item, fieldDefinitions, isVisible)
        : {singleSelect: [], multiSelect: []},
    created: isVisible(FieldIdentifier.CREATED) ? (item.fields.created?.value ?? null) : null,
    updated: isVisible(FieldIdentifier.UPDATED) ? (item.fields.updated?.value ?? null) : null,
    closed: isVisible(FieldIdentifier.CLOSED) ? (item.fields.closed?.value ?? null) : null,
  }
}

function resolveIssueFields(
  item: BoardPayloadIssueItem,
  fieldDefinitions: readonly FieldDefinition[],
  isVisible: (identifier: string) => boolean,
): CardIssueFields {
  const singleSelect: CardSingleSelect[] = []
  const multiSelect: CardMultiSelect[] = []
  for (const {identifier, dataType} of fieldDefinitions) {
    if (!isCustomFieldIdentifier(identifier) || !isVisible(identifier)) continue
    const value = item.fields[identifier]?.value
    if (dataType === FieldDataType.SINGLE_SELECT && isBoardPayloadSelectOption(value)) {
      singleSelect.push({identifier, option: value})
    } else if (dataType === FieldDataType.MULTI_SELECT && Array.isArray(value)) {
      const options = value.filter(isBoardPayloadSelectOption)
      if (options.length > 0) multiSelect.push({identifier, options})
    }
  }
  return {singleSelect, multiSelect}
}

function isCustomFieldIdentifier(identifier: string): identifier is CustomFieldIdentifier {
  return /^field\.\d+$/.test(identifier)
}
